import {
  TransitionConfigOrTarget,
  assign,
  choose,
  send,
  sendTo,
} from '@monofront/xstate-v4';
import { SELECT_SHIPPING_METHOD_TRANSITIONS } from './selectShippingMethod';
import { SELECT_ADDRESS_TRANSITIONS } from './selectAddress';
import { CHECKOUT_STATES } from './states';
import {
  CheckoutCanCheckoutEvent,
  CheckoutCartMachineDoneEvent,
  CheckoutUserReadyEvent,
  ICheckoutMachineContext,
  ICheckoutMachineEvents,
  ISelectBillingAddressEvent,
  ISelectShippingAddressEvent,
} from './types';
import { isDeliveryInfoUpdated } from './guards';
import invariant from 'tiny-invariant';

export const assignCart = assign<
  ICheckoutMachineContext,
  CheckoutCartMachineDoneEvent
>({
  checkoutCart: (_, event) => {
    invariant('cart' in event.data);

    return event.data.cart;
  },
});

export const resetCheckoutCartId = assign<ICheckoutMachineContext>({
  checkoutCartId: null,
  checkoutCart: null,
});

export const assignUserReady = assign<
  ICheckoutMachineContext,
  CheckoutCanCheckoutEvent | CheckoutUserReadyEvent
>({
  isUserReady: (_, event) => event.isUserReady,
});

export const assignShippingAndBillingAddress = assign<
  ICheckoutMachineContext,
  ISelectShippingAddressEvent
>({
  shippingAddressSelected: (_, event) => event.shippingAddress,
  billingAddressSelected: (_, event) => event.billingAddress,
});

export const assignBillingAddress = assign<
  ICheckoutMachineContext,
  ISelectBillingAddressEvent
>({
  billingAddressSelected: (_, event) => event.billingAddress,
});

export const sendFetchShippingMethods = send<ICheckoutMachineContext, any>(
  {
    type: SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS,
    resetShippingMethodSelection: false,
  },
  {
    to: (context) => context.selectShippingMethodRef!,
  },
);

export const sendShippingAddress = send<ICheckoutMachineContext, any>(
  (_, event) => ({
    type: SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS,
    shippingAddressSelected: event.shippingAddress,
  }),
  {
    to: (context) => context.selectShippingMethodRef!,
  },
);

export const sendCheckoutCartId = sendTo(
  (context: ICheckoutMachineContext) => {
    invariant(
      context.selectShippingMethodRef,
      'selectShippingMethodRef must be set while sending SYNC_CART_DONE event',
    );

    return context.selectShippingMethodRef;
  },
  (_, event: CheckoutCartMachineDoneEvent) => {
    invariant('cart' in event.data);

    return {
      type: SELECT_SHIPPING_METHOD_TRANSITIONS.SYNC_CART_DONE,
      checkoutCartId: event.data.cart.id,
    };
  },
);

export const sendShippingAddressWithUpdatedDeliveryInfos = choose([
  {
    cond: isDeliveryInfoUpdated,
    actions: send<ICheckoutMachineContext, any>(
      (context, event) => {
        return {
          type: SELECT_ADDRESS_TRANSITIONS.SET_DELIVERY_INFOS,
          deliveryInstruction:
            event.data.data.selectHomeShippingMethod?.shipments?.[0]
              ?.deliveryInstruction ??
            event.data.data.selectCourierShippingMethod?.shipments?.[0]
              ?.deliveryInstruction,
          selectedShippingAddressHash: context.shippingAddressSelected?.hash,
        };
      },
      {
        to: (context) => context.selectAddressesRef!,
      },
    ),
  },
]);

export enum CHECKOUT_EXTERNAL_ACTIONS {
  syncCart = 'syncCart',
  spawnMachines = 'spawnMachines',
  syncedCart = 'syncedCart',
  syncCartError = 'syncCartError',
  postShippingError = 'postShippingError',
  postBillingError = 'postBillingError',
  postShippingMethodError = 'postShippingMethodError',
}

export const selectShippingAddressTransition = {
  target: CHECKOUT_STATES.WAITING_CART,
  actions: [assignShippingAndBillingAddress, sendShippingAddress],
};

export const selectBillingAddressTransition = {
  target: CHECKOUT_STATES.POST_BILLING_ADDRESS_SELECTED,
  actions: [assignBillingAddress],
};

export const restartCheckoutTransition = {
  target: CHECKOUT_STATES.INITIAL,
  actions: [resetCheckoutCartId],
} as TransitionConfigOrTarget<ICheckoutMachineContext, ICheckoutMachineEvents>;
